import React, { ComponentProps, ComponentType, useEffect, useState } from 'react';
import useFeatureFlag from '@/components/hooks/useFeatureFlag';
import { CLIENT_FEATURE_FLAGS, AUTHENTICATED_USER_SESSION_STATE } from '@/constants';
import OverlaySpinner from '@/components/OverlaySpinner';
import logger from '@/lib/clientLogger';
import { AppPageComponent } from '@/types/app';
import { EnrollmentAppProps } from '@/pages/_app';

/**
 * This HOC redirects authorized users from the page.
 * Target URLs are defined in the `REDIRECT_URLS` constant for different user roles.
 * To control this behavior use the `growth-enrollment-logged-in-user-redirect` LD flag.
 * If the LD flag is turned on the authorized users will be redirected, otherwise not.
 */
export default function withOnlyUnauthorizedUsers<
  P extends ComponentProps<EnrollmentAppProps['Component']>,
>(Component: ComponentType<P>, redirectUrl: string): ComponentType<P> {
  function OnlyUnauthorizedUsers(props: P) {
    const { userCareSession, hasCSCSessionCookie } = props;
    const [isLoaded, setIsLoaded] = useState(false);
    const loggedInRedirectFlag = useFeatureFlag(
      CLIENT_FEATURE_FLAGS.GROWTH_ENROLLMENT_LOGGED_IN_REDIRECT
    );
    const eligibleForRedirect =
      hasCSCSessionCookie === true && userCareSession?.state === AUTHENTICATED_USER_SESSION_STATE;
    const isLoggedInRedirect = loggedInRedirectFlag?.value === 2;

    useEffect(() => {
      if (isLoggedInRedirect) {
        logger.info({
          event: 'userAuthenticationStatus',
          isCscSession: hasCSCSessionCookie,
          fastifySessionAuthentication: userCareSession?.state,
          eligibleForRedirect,
        });

        if (eligibleForRedirect === true) {
          const queryParams = window?.location?.search || '';
          window.location.assign(redirectUrl + queryParams);
          return;
        }
      }
      setIsLoaded(true);
    }, []);

    const isLoading = isLoggedInRedirect && !isLoaded;
    if (isLoading) {
      return <OverlaySpinner isOpen wrapped />;
    }

    return <Component {...props} />;
  }

  OnlyUnauthorizedUsers.displayName = `withOnlyUnauthorizedUsers(${
    Component.displayName || Component.name || /* istanbul ignore next */ 'Component'
  })`;
  OnlyUnauthorizedUsers.disableScreenViewed = (Component as AppPageComponent).disableScreenViewed;
  OnlyUnauthorizedUsers.Layout = (Component as AppPageComponent).Layout;
  OnlyUnauthorizedUsers.Header = (Component as AppPageComponent).Header;
  OnlyUnauthorizedUsers.Footer = (Component as AppPageComponent).Footer;

  return OnlyUnauthorizedUsers;
}
